import { toJS } from 'mobx';

import { ContextStore } from 'bernie-plugin-mobx';

import {
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsAdvancedFilterType,
  FlightsTripType,
  FlightsCabinClass,
  FlightsTravelerType,
  FlightsDetailCriteriaInput,
  FlightsSearchFilterValuesInput,
  FlightsJourneyCriteriaInput,
  FlightsTravelerDetailsInput,
  RewardsOption
} from '__generated__/api';
import { formatDateInputIntoString, getPackageTypeSymbol } from '.';
import { DeepLinkInformationType, DeepLinkType, SearchParametersType } from '../types/common';

const TripType = {
  [FlightsTripType.MULTI_DESTINATION]: 'multi',
  [FlightsTripType.ONE_WAY]: 'oneway',
  [FlightsTripType.ROUND_TRIP]: 'roundtrip'
};

const CabinClass = {
  [FlightsCabinClass.BUSINESS]: 'business',
  [FlightsCabinClass.COACH]: 'coach',
  [FlightsCabinClass.FIRST]: 'first',
  [FlightsCabinClass.PREMIUM_ECONOMY]: 'premium_economy'
};

const getPreferredAirline = ({ airlinePreferenceCode }: SearchParametersType) =>
  airlinePreferenceCode === '' ? '' : airlinePreferenceCode;

const getPreferredClass = ({ flightAirCabinClass }: SearchParametersType) =>
  flightAirCabinClass === FlightsCabinClass.COACH ? '' : CabinClass[flightAirCabinClass];

const getStopPreference = ({ nonstopOnly }: SearchParametersType) => nonstopOnly;

const getPenaltyPreference = ({ refundableFlightsOnly }: SearchParametersType) => refundableFlightsOnly;

const getAdultCount = ({ travelerCategoryInfo }: SearchParametersType) => travelerCategoryInfo.adultCount;

const getInfantInfo = ({ travelerCategoryInfo }: SearchParametersType) =>
  travelerCategoryInfo.infantSeatingOnLap ? 'Y' : 'N';

const getChildInfo = ({ travelerCategoryInfo }: SearchParametersType) => {
  const { childCount } = travelerCategoryInfo;
  const childAges = travelerCategoryInfo.childAges
    .filter(age => {
      return age !== -1;
    })
    .join(';');

  return childCount > 0 ? `${childCount}[${childAges}]` : childCount;
};

const paramToFunctionMap = {
  options: {
    'carrier:': getPreferredAirline,
    'cabinclass:': getPreferredClass,
    'maxhops:': getStopPreference,
    'nopenalty:': getPenaltyPreference
  },
  passengers: {
    'adults:': getAdultCount,
    'children:': getChildInfo,
    'infantinlap:': getInfantInfo
  }
};

const generateOptionsArray = (deepLinkObject: DeepLinkType, searchParameters: SearchParametersType, type) => {
  deepLinkObject[type] = Object.keys(paramToFunctionMap[type])
    .map(option => `${option}${paramToFunctionMap[type][option](searchParameters)}`)
    .join(',');
};

const addOptions = (deepLinkObject: DeepLinkType, searchParameters: SearchParametersType) => {
  generateOptionsArray(deepLinkObject, searchParameters, 'options');
};

const addPassengers = (deepLinkObject: DeepLinkType, searchParameters: SearchParametersType) => {
  generateOptionsArray(deepLinkObject, searchParameters, 'passengers');
};

const addLegs = (deepLinkObject: DeepLinkType, { legSearchInfoList }: SearchParametersType, context: ContextStore) => {
  legSearchInfoList.forEach((leg, index) => {
    const legStringValue = `from:${leg.departureLocation},to:${
      leg.arrivalLocation
    },departure:${formatDateInputIntoString(leg.departureDate, context)}TANYT`;

    deepLinkObject[`leg${index + 1}`] = legStringValue;
  });
};

const addFilters = (deepLinkObject: DeepLinkType, { searchFilterValuesList }: SearchParametersType) => {
  const filters = toJS(searchFilterValuesList) as FlightsSearchFilterValuesInput[];

  if (filters.length > 0) {
    deepLinkObject.filters = JSON.stringify(filters);
  }
};

const addSort = (deepLinkObject: DeepLinkType, { sortOption }: SearchParametersType) => {
  const sort = toJS(sortOption);

  if (sort && sort.sortOrder && sort.sortType) {
    deepLinkObject.sortOrder = sort.sortOrder;
    deepLinkObject.sortType = sort.sortType;
  }
};

const addTripType = (deepLinkObject: DeepLinkType, { flightRouteType }: SearchParametersType) =>
  (deepLinkObject.trip = TripType[flightRouteType]);

const addJCID = (deepLinkObject: DeepLinkType, { journeysContinuationId }: SearchParametersType) => {
  if (journeysContinuationId) {
    deepLinkObject.journeysContinuationId = journeysContinuationId;
  }
};

const addOBID = (deepLinkObject: DeepLinkType, { originalBookingId }: SearchParametersType) => {
  if (originalBookingId) {
    deepLinkObject.originalBookingId = originalBookingId;
  }
};

const addMisId = (deepLinkObject: DeepLinkType, { misId }: SearchParametersType) => {
  deepLinkObject.misId = misId;
};

const addPackageType = (deepLinkObject: DeepLinkType, { packageType }: SearchParametersType) => {
  deepLinkObject.packageType = packageType;
};

export const generateDeeplink = (
  searchParameters: SearchParametersType,
  context: ContextStore,
  pageId?: string,
  removePageIdParam?: boolean,
  multiItemPriceToken?: string,
  rewardsOption?: RewardsOption,
  client?: string,
  opaqueToken?: string,
  itineraryNumber?: string
) => {
  const deepLink: DeepLinkType = {
    mode: 'search'
  };

  addJCID(deepLink, searchParameters);
  addOBID(deepLink, searchParameters);
  addTripType(deepLink, searchParameters);
  addLegs(deepLink, searchParameters, context);
  addPassengers(deepLink, searchParameters);
  addOptions(deepLink, searchParameters);
  addFilters(deepLink, searchParameters);
  addSort(deepLink, searchParameters);
  if (searchParameters.misId) {
    addMisId(deepLink, searchParameters);
  }
  if (searchParameters.packageType) {
    addPackageType(deepLink, searchParameters);
  }
  if (rewardsOption) {
    deepLink.rewardsOption = rewardsOption;
  }
  if (multiItemPriceToken) {
    deepLink.mipt = multiItemPriceToken;
  }

  if (pageId && !removePageIdParam) {
    deepLink.pageId = pageId;
  }

  if (client) {
    deepLink.client = client;
  }
  if (opaqueToken) {
    deepLink.opaqueToken = opaqueToken;
  }
  if (itineraryNumber) {
    deepLink.itineraryNumber = itineraryNumber;
  }

  return deepLink;
};

export const generateInformationDeeplink = (flightsDetailCriteria: FlightsDetailCriteriaInput) => {
  const deepLink: DeepLinkInformationType = {
    journeyContinuationId: flightsDetailCriteria && flightsDetailCriteria.journeyContinuationId
  };

  return deepLink;
};

export const createSearchParameters = ({
  flightsSearchContext,
  travelerDetails,
  journeyCriteria,
  searchPreferences,
  searchFilterValuesList,
  sortOption,
  shoppingContext
}: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables): SearchParametersType => {
  const flightRouteType = flightsSearchContext.tripType || '';
  const journeysContinuationId = flightsSearchContext.journeysContinuationId || null;
  const originalBookingId = flightsSearchContext.originalBookingId || null;
  const previousOriginalBookingId = flightsSearchContext.previousOriginalBookingId || null;

  const legSearchInfoList = (journeyCriteria as FlightsJourneyCriteriaInput[]).map(leg => ({
    departureLocation: leg.origin,
    arrivalLocation: leg.destination,
    departureDate: toJS(leg.departureDate)
  }));
  const normalizedTravelerDetails = travelerDetails as FlightsTravelerDetailsInput[];
  let travelerCategoryInfo;
  if (normalizedTravelerDetails.length > 0) {
    const child = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.CHILD);
    const adult = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.ADULT);
    const infantsInSeat = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.INFANT_IN_SEAT);
    const infantsInLap = normalizedTravelerDetails.find(e => e.travelerType === FlightsTravelerType.INFANT_IN_LAP);
    const adultCount = adult ? adult.count : 0;
    const totalPassengers = normalizedTravelerDetails.reduce((a, { count }) => count + a, 0);
    const infantSeatingOnLap = infantsInLap !== undefined;
    let childAges = child ? toJS(child.ages) : [];
    let childCount = child ? child.count : 0;

    if (infantsInSeat) {
      childCount += infantsInSeat.count;
      childAges = childAges.concat(toJS(infantsInSeat.ages));
    }

    if (infantsInLap) {
      childCount += infantsInLap.count;
      childAges = childAges.concat(toJS(infantsInLap.ages));
    }

    travelerCategoryInfo = {
      adultCount,
      childAges,
      childCount,
      infantSeatingOnLap,
      totalPassengers
    };
  }

  const airlinePreferenceCode = searchPreferences.airline || '';
  const flightAirCabinClass = searchPreferences.cabinClass || '';
  const refundableFlightsOnly =
    searchPreferences.advancedFilters &&
    searchPreferences.advancedFilters.find(e => e === FlightsAdvancedFilterType.REFUNDABLE)
      ? 'Y'
      : 'N';
  const nonstopOnly =
    searchPreferences.advancedFilters &&
    searchPreferences.advancedFilters.find(e => e === FlightsAdvancedFilterType.NONSTOP)
      ? 0
      : 1;

  const misId = shoppingContext?.multiItem?.id || null;
  const packageType = getPackageTypeSymbol(shoppingContext?.multiItem?.packageType) || null;

  return {
    flightRouteType,
    legSearchInfoList,
    travelerCategoryInfo,
    airlinePreferenceCode,
    flightAirCabinClass,
    refundableFlightsOnly,
    nonstopOnly,
    searchFilterValuesList,
    sortOption,
    journeysContinuationId,
    originalBookingId,
    previousOriginalBookingId,
    misId,
    packageType
  };
};
